import * as React from 'react';
import { Rectangle } from 'ark-common';
import { Vector2D } from '../../types/common';
import { UIEvent } from '../../uievent';
import { getClientPoint } from '../../drawing/util/getClientPoint';

const rectAnimationInterval = 500;
const rectDashInterval = 5;
const resizingCursorRange = 8;

interface PictureCropperProps {
  scaling: number;
  translation: Vector2D;
  clientWidth: number;
  clientHeight: number;
  pictureURL: string;
  pictureWidth: number;
  pictureHeight: number;
  croppingRect: Rectangle;
  onChangeCroppingRect: (rect: Rectangle) => void;
}

enum PictureCropperCursorMode {
  None,
  Moving,
  ResizingTop,
  ResizingTopRight,
  ResizingRight,
  ResizingBottomRight,
  ResizingBottom,
  ResizingBottomLeft,
  ResizingLeft,
  ResizingTopLeft,
}

interface PictureCropperWorkNone {
  dragging: false;
}

interface PictureCropperWorkDragging {
  dragging: true;
  anchor: Vector2D;
  prevRect: Rectangle;
}

interface PictureCropperState {
  cursorMode: PictureCropperCursorMode;
  work: PictureCropperWorkNone | PictureCropperWorkDragging;
  rectDashOffset: number;
}

function moveRect(rect: Rectangle, offset: Vector2D): Rectangle {
  return { ...rect, x: rect.x + offset.x, y: rect.y + offset.y };
}

function resizeRectLeft(rect: Rectangle, offset: number): Rectangle {
  return { ...rect, x: rect.x + offset, width: rect.width - offset };
}

function resizeRectTop(rect: Rectangle, offset: number): Rectangle {
  return { ...rect, y: rect.y + offset, height: rect.height - offset };
}

function resizeRectRight(rect: Rectangle, offset: number): Rectangle {
  return { ...rect, width: rect.width + offset };
}

function resizeRectBottom(rect: Rectangle, offset: number): Rectangle {
  return { ...rect, height: rect.height + offset };
}

export default class PictureCropper
  extends React.Component<PictureCropperProps, PictureCropperState> {
  private display: SVGElement | null;
  private intervalID: number | null;

  private clientToPicture(pos: Vector2D): Vector2D {
    const { scaling, translation } = this.props;
    return {
      x: (pos.x - translation.x) / scaling,
      y: (pos.y - translation.y) / scaling,
    };
  }

  private workModeFromPicturePoint(pos: Vector2D) {
    const { x, y, width, height } = this.props.croppingRect;
    const right = x + width;
    const bottom = y + height;

    if ((pos.x < x - resizingCursorRange || right + resizingCursorRange < pos.x)
      || (pos.y < y - resizingCursorRange || bottom + resizingCursorRange < pos.y)) {
      return PictureCropperCursorMode.None;
    }

    const horzInset = Math.min(resizingCursorRange, width / 2);
    const vertInset = Math.min(resizingCursorRange, height / 2);
    const isRight = (right - horzInset <= pos.x);
    const isLeft = (pos.x <= x + horzInset);

    if (pos.y <= y + vertInset) {
      if (isLeft) {
        return PictureCropperCursorMode.ResizingTopLeft;
      }

      if (isRight) {
        return PictureCropperCursorMode.ResizingTopRight;
      }

      return PictureCropperCursorMode.ResizingTop;
    }

    if (pos.y >= bottom - vertInset) {
      if (isLeft) {
        return PictureCropperCursorMode.ResizingBottomLeft;
      }

      if (isRight) {
        return PictureCropperCursorMode.ResizingBottomRight;
      }

      return PictureCropperCursorMode.ResizingBottom;
    }

    if (isLeft) {
      return PictureCropperCursorMode.ResizingLeft;
    }

    if (isRight) {
      return PictureCropperCursorMode.ResizingRight;
    }

    return PictureCropperCursorMode.Moving;
  }

  private handlePointerDown = (ev: UIEvent<SVGElement>) => {
    ev.preventDefault();

    const pos = this.clientToPicture(getClientPoint(ev));
    const cursorMode = this.workModeFromPicturePoint(pos);
    if (cursorMode !== PictureCropperCursorMode.None) {
      this.setCapture();
      this.setState({
        cursorMode,
        work: { dragging: true, anchor: pos, prevRect: this.props.croppingRect },
      });
    }
  }

  private handleMouseMoveDirect = (ev: UIEvent<SVGElement>) => {
    ev.preventDefault();

    if (this.state.work.dragging) {
      return;
    }

    this.setState({
      cursorMode: this.workModeFromPicturePoint(this.clientToPicture(getClientPoint(ev))),
    });
  }

  private doHandlePointerMove(pos: Vector2D) {
    if (!this.state.work.dragging) {
      throw new Error('Illegal state.');
    }

    const { pictureWidth, pictureHeight } = this.props;
    const { anchor, prevRect } = this.state.work;
    const diff = { x: pos.x - anchor.x, y: pos.y - anchor.y };

    const minLeft = -prevRect.x;
    const maxLeft = Math.max(0, prevRect.width - 1);
    const minTop = -prevRect.y;
    const maxTop = Math.max(0, prevRect.height - 1);
    const minRight = Math.min(0, -(prevRect.width - 1));
    const maxRight = pictureWidth - (prevRect.x + prevRect.width);
    const minBottom = Math.min(0, -(prevRect.height - 1));
    const maxBottom = pictureHeight - (prevRect.y + prevRect.height);

    let nextRect: Rectangle;
    switch (this.state.cursorMode) {
      case PictureCropperCursorMode.None:
        return;

      case PictureCropperCursorMode.Moving:
        nextRect = moveRect(prevRect, {
          x: Math.max(minLeft, Math.min(diff.x, maxRight)),
          y: Math.max(minTop, Math.min(diff.y, maxBottom)),
        });
        break;

      case PictureCropperCursorMode.ResizingTop:
        nextRect = resizeRectTop(prevRect, Math.max(minTop, Math.min(diff.y, maxTop)));
        break;

      case PictureCropperCursorMode.ResizingTopRight:
        nextRect = resizeRectTop(
          resizeRectRight(prevRect, Math.max(minRight, Math.min(diff.x, maxRight))),
          Math.max(minTop, Math.min(diff.y, maxTop)),
        );
        break;

      case PictureCropperCursorMode.ResizingRight:
        nextRect = resizeRectRight(prevRect, Math.max(minRight, Math.min(diff.x, maxRight)));
        break;

      case PictureCropperCursorMode.ResizingBottomRight:
        nextRect = resizeRectBottom(
          resizeRectRight(prevRect, Math.max(minRight, Math.min(diff.x, maxRight))),
          Math.max(minBottom, Math.min(diff.y, maxBottom)),
        );
        break;

      case PictureCropperCursorMode.ResizingBottom:
        nextRect = resizeRectBottom(prevRect, Math.max(minBottom, Math.min(diff.y, maxBottom)));
        break;

      case PictureCropperCursorMode.ResizingBottomLeft:
        nextRect = resizeRectBottom(
          resizeRectLeft(prevRect, Math.max(minLeft, Math.min(diff.x, maxLeft))),
          Math.max(minBottom, Math.min(diff.y, maxBottom)),
        );
        break;

      case PictureCropperCursorMode.ResizingLeft:
        nextRect = resizeRectLeft(prevRect, Math.max(minLeft, Math.min(diff.x, maxLeft)));
        break;

      case PictureCropperCursorMode.ResizingTopLeft:
        nextRect = resizeRectTop(
          resizeRectLeft(prevRect, Math.max(minLeft, Math.min(diff.x, maxLeft))),
          Math.max(minTop, Math.min(diff.y, maxTop)),
        );
        break;

      // Somehow it is needed.
      default:
        throw new Error('Illegal state.');
    }

    this.props.onChangeCroppingRect(nextRect);
  }

  private handleMouseMoveDragging = (ev: MouseEvent) => {
    ev.preventDefault();

    if (this.display !== null) {
      const clientRect = this.display.getBoundingClientRect();
      this.doHandlePointerMove(this.clientToPicture({
        x: ev.pageX - clientRect.left,
        y: ev.pageY - clientRect.top,
      }));
    }
  }

  private handleTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();

    if (this.display !== null) {
      const clientRect = this.display.getBoundingClientRect();
      const touch = ev.changedTouches[0];
      this.doHandlePointerMove(this.clientToPicture({
        x: touch.pageX - clientRect.left,
        y: touch.pageY - clientRect.top,
      }));
    }
  }

  private handlePointerUp = (ev: MouseEvent | TouchEvent) => {
    ev.preventDefault();
    this.releaseCapture();
    this.setState({ work: { dragging: false } });
  }

  private setCapture() {
    if (this.display === null) {
      return;
    }

    const { ownerDocument } = this.display;
    if (ownerDocument === null) {
      return;
    }

    ownerDocument.addEventListener('mousemove', this.handleMouseMoveDragging);
    ownerDocument.addEventListener('mouseup', this.handlePointerUp);
    ownerDocument.addEventListener('touchmove', this.handleTouchMove);
    ownerDocument.addEventListener('touchend', this.handlePointerUp);
  }

  private releaseCapture() {
    if (this.display === null) {
      return;
    }

    const { ownerDocument } = this.display;
    if (ownerDocument === null) {
      return;
    }

    ownerDocument.removeEventListener('mousemove', this.handleMouseMoveDragging);
    ownerDocument.removeEventListener('mouseup', this.handlePointerUp);
    ownerDocument.removeEventListener('touchmove', this.handleTouchMove);
    ownerDocument.removeEventListener('touchend', this.handlePointerUp);
  }

  private handleRectAnimation = () => {
    this.setState({ rectDashOffset: (this.state.rectDashOffset + 1) % (rectDashInterval * 2) });
  }

  private cursor(): string {
    switch (this.state.cursorMode) {
      case PictureCropperCursorMode.None:
        return 'auto';
      case PictureCropperCursorMode.Moving:
        return 'move';
      case PictureCropperCursorMode.ResizingTop:
        return 'n-resize';
      case PictureCropperCursorMode.ResizingTopRight:
        return 'ne-resize';
      case PictureCropperCursorMode.ResizingRight:
        return 'e-resize';
      case PictureCropperCursorMode.ResizingBottomRight:
        return 'se-resize';
      case PictureCropperCursorMode.ResizingBottom:
        return 's-resize';
      case PictureCropperCursorMode.ResizingBottomLeft:
        return 'sw-resize';
      case PictureCropperCursorMode.ResizingLeft:
        return 'w-resize';
      case PictureCropperCursorMode.ResizingTopLeft:
        return 'nw-resize';
    }
  }

  public componentDidMount() {
    this.intervalID = window.setInterval(this.handleRectAnimation, rectAnimationInterval);
  }

  public componentWillUnmount() {
    this.releaseCapture();
    window.clearInterval(this.intervalID!);
  }

  public render() {
    const { scaling, translation, pictureURL, croppingRect } = this.props;

    const svgStyle: React.CSSProperties = {
      position: 'absolute',
      cursor: this.cursor(), // FIXME : Apply this globally during dragging.
    };

    const rect = {
      x: Math.floor(scaling * croppingRect.x + translation.x),
      y: Math.floor(scaling * croppingRect.y + translation.y),
      width: Math.ceil(scaling * croppingRect.width),
      height: Math.ceil(scaling * croppingRect.height),
      fill: 'none',
      strokeWidth: 1,
    };

    return (
      <svg ref={e => this.display = e} style={svgStyle}
        width={this.props.clientWidth} height={this.props.clientHeight}
        onMouseDown={this.handlePointerDown}
        onMouseMove={this.handleMouseMoveDirect}
        onTouchStart={this.handlePointerDown}>
        <image key={pictureURL} xlinkHref={pictureURL} x={translation.x} y={translation.y}
          width={scaling * this.props.pictureWidth} height={scaling * this.props.pictureHeight} />
        <rect {...rect} stroke="white" />
        <rect {...rect} stroke="black"
          strokeDasharray={`${rectDashInterval},${rectDashInterval}`}
          strokeDashoffset={this.state.rectDashOffset} />
      </svg>
    );
  }

  public constructor(props: Readonly<PictureCropperProps>) {
    super(props);
    this.display = null;
    this.intervalID = null;
    this.state = {
      cursorMode: PictureCropperCursorMode.None,
      work: { dragging: false },
      rectDashOffset: 0,
    };
  }
}
